import { message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { decodeToken } from 'react-jwt';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import { environments } from '../constants';
import { parseError } from '../utils/parseError';
import { useLoginMutationeArgs } from '../models';
import { LOGIN, VERIFICATION_CODE } from '../graphql/Mutations';
import {
  UserException,
  allowedUserType,
  populateSessionStorage,
} from '../utils/helpers';
import { useAuthStore } from '../store';
import {
  add_member_to_organisation,
  get_all_user_data_v2,
  get_username,
} from '../services/AuthActions';
import { ESessionStorageKeys } from '../../../types';
import mixpanelService from '../services/mixpanel';
import bugfenderHelper from '../services/bugfender';
import {
  EMixPanelAuthTypes,
  EMixPanelMetadataKeys,
} from '../../types';

type IVerificationData = {
  accessToken: string;
  IdToken: string;
  userData: any;
};

export const useLoginMutation = ({
  isAfterSignUp = false,
  variable,
  ssoData,
  addMemberPayload,
  canAddMember = false,
  onCompletedCallback,
  nextStep,
}: useLoginMutationeArgs) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hookLoading, setHookLoading] = useState(false);
  const [verificationData, setVerificationData] =
    useState<IVerificationData | null>(null);

  const [
    loginMutation,
    {
      loading: loginLoading,
      data: loginData,
      error: loginError,
      reset: loginReset,
    },
  ] = useMutation(LOGIN);

  const [
    verificationCodeMutation,
    {
      loading: verificationCodeLoading,
      data: verificationCodeData,
      error: verificationCodeError,
    },
  ] = useMutation(VERIFICATION_CODE);

  const setAuthChallengeState = useAuthStore((s) => s.setAuthChallengeState);
  const setLoginInputs = useAuthStore((s) => s.setLoginInputs);
  const setLoginTokens = useAuthStore((s) => s.setLoginTokens);
  const signUpFormInputs = useAuthStore((s) => s.signUpInputs);
  const appType = useAuthStore((s) => s.currentAppState);

  const handlePhoneNumberVerificationCheck = async ({
    accessToken,
    IdToken,
    userData,
  }: IVerificationData): Promise<void> => {
    try {
      const jwtPayloadIdToken: any = decodeToken(IdToken);
      const isPhoneNumberVerified = jwtPayloadIdToken?.phone_number_verified;

      if (!isPhoneNumberVerified) {
        message.info(
          'Your phone number is not verified! Kindly verify by following this steps'
        );

        verificationCodeMutation({
          variables: {
            accessToken,
            attributeName: 'phone_number',
          },
        });
      } else {
        setHookLoading(false);
        navigate(`/login-redirect/tripplo-mobile-auth/${userData}`);
      }
    } catch {
      message.error("We've encountered an error verifying your phone number");
    }
  };

  const handleLogin = useCallback(
    async (username?: string, password?: string) => {
      const response = await loginMutation({
        variables: {
          username: username ?? variable?.username?.toLowerCase(),
          password: password ?? variable?.password,
          clientId: environments.REACT_APP_DEPLOY_USER_POOL_WEB_CLIENT_ID,
        },
      });
      return response?.data;
    },
    [variable?.password, variable?.username]
  );

  const handleAddMemberToOrg = async (username: string, jwt: string) => {
    try {
      await add_member_to_organisation(
        'driver',
        addMemberPayload?.firstName || '',
        addMemberPayload?.lastName || '',
        addMemberPayload?.gender || '',
        addMemberPayload?.email?.toLowerCase() || '',
        addMemberPayload?.phoneNumber || '',
        username,
        addMemberPayload?.orgId || '',
        jwt
      );
      if (username) {
        mixpanelService.track('App.Add.Driver.To.Org', {
          org_id: addMemberPayload?.orgId || '',
          driver_id: username,
        });
      }
    } catch (error) {
      console.error('Error adding driver to organization:', error);
      bugfenderHelper.error(error);
    }
  };

  useEffect(() => {
    if (!loginData) return;

    if (loginData?.login?.Error) {
      setLoading(false);
      message.error(loginData?.login?.Error?.message);
      loginReset();
      return;
    }

    const processLogin = async () => {
      try {
        setHookLoading(true);

        if (loginData?.login) {
          mixpanelService.track('App.Login', {
            username: variable?.username,
            clientId: environments.REACT_APP_DEPLOY_USER_POOL_WEB_CLIENT_ID,
          });

          if (loginData?.login?.MFAEnabled) {
            setAuthChallengeState(loginData.login);
            setLoginInputs(variable);

            message.success(
              'We have resent your OTP code to your preferred communication method. Please check and submit the code for verification to proceed.'
            );

            setHookLoading(false);
            onCompletedCallback();
          } else {
            if (isAfterSignUp) {
              setLoginTokens({
                jwt: loginData?.login?.AuthenticationResult?.IdToken,
                refresh: loginData?.login?.AuthenticationResult?.RefreshToken,
              });

              populateSessionStorage([
                {
                  key: ESessionStorageKeys.AUTH_TOKEN,
                  value: loginData?.login?.AuthenticationResult?.IdToken,
                },
                {
                  key: ESessionStorageKeys.REFRESH_TOKEN,
                  value: loginData?.login?.AuthenticationResult?.RefreshToken,
                },
                {
                  key: ESessionStorageKeys.ID_TOKEN,
                  value: loginData?.login?.AuthenticationResult?.IdToken,
                },
              ]);

              nextStep?.();
            } else {
              setLoginTokens({
                jwt: loginData?.login?.AuthenticationResult?.AccessToken,
                refresh: loginData?.login?.AuthenticationResult?.RefreshToken,
              });

              const userTypeCheck = await allowedUserType(
                loginData?.login?.AuthenticationResult?.AccessToken,
                appType
              );

              if (!userTypeCheck) {
                return message.error(
                  'Your account type is currently not supported'
                );
              }

              const username = await get_username(
                loginData?.login?.AuthenticationResult?.AccessToken
              );

              if (!username) {
                throw UserException(
                  'get_username - Error encountered retrieving username'
                );
              }

              let organisationDetailsObject: any;

              if (canAddMember) {
                await new Promise<void>((resolve) => {
                  handleAddMemberToOrg(
                    username,
                    loginData?.login?.AuthenticationResult?.AccessToken
                  );
                  setTimeout(async () => {
                    organisationDetailsObject =
                      await get_all_user_data_v2(username);
                    resolve();
                  }, 5000);
                });
              } else {
                organisationDetailsObject = await get_all_user_data_v2(username);
              }

              if (!organisationDetailsObject) {
                throw UserException(
                  'get_all_user_data - Error encountered getting all user data'
                );
              }

              const allUserData = organisationDetailsObject[0];

              const userData = JSON.stringify({
                token: loginData?.login?.AuthenticationResult?.IdToken,
                refresh: loginData?.login?.AuthenticationResult?.RefreshToken,
                sub: username,
                org_id: allUserData.org_id,
              });

              populateSessionStorage([
                {
                  key: ESessionStorageKeys.AUTH_TOKEN,
                  value: loginData?.login?.AuthenticationResult?.AccessToken,
                },
                {
                  key: ESessionStorageKeys.REFRESH_TOKEN,
                  value: loginData?.login?.AuthenticationResult?.RefreshToken,
                },
                {
                  key: ESessionStorageKeys.ID_TOKEN,
                  value: loginData?.login?.AuthenticationResult?.IdToken,
                },
                {
                  key: ESessionStorageKeys.ORG_ID,
                  value: allUserData.org_id,
                },
              ]);

              if (username && allUserData?.org_id) {
                mixpanelService.track('v1.Tripplo.SSO.Login.Success', {
                  username: username,
                  clientId: environments.REACT_APP_DEPLOY_USER_POOL_WEB_CLIENT_ID,
                  orgId: allUserData.org_id,
                  [EMixPanelMetadataKeys.AUTH_TYPE]:
                    EMixPanelAuthTypes.WEB_LOGIN_BASE,
                  [EMixPanelMetadataKeys.DEPLOY_STAGE]:
                    environments.REACT_APP_DEPLOY_STAGE,
                });
              }

              setVerificationData({
                accessToken:
                  loginData?.login?.AuthenticationResult?.AccessToken,
                IdToken: loginData?.login?.AuthenticationResult?.IdToken,
                userData: userData,
              });

              await handlePhoneNumberVerificationCheck({
                accessToken:
                  loginData?.login?.AuthenticationResult?.AccessToken,
                IdToken: loginData?.login?.AuthenticationResult?.IdToken,
                userData,
              });
            }
          }
        } else {
          if (isAfterSignUp) {
            setHookLoading(false);
            navigate('/login/mobile-app/MzRlNTgzNGNkMzVka3J2anAwcGpkanAxYmM=');
          } else {
            setHookLoading(false);
            message.error(
              'Something went wrong... Please contact our support services'
            );
          }
        }
      } catch (err) {
        console.error('useLoginMutation - Error encountered', err);
        setHookLoading(false);
      }
    };

    processLogin();
  }, [variable, loginData, isAfterSignUp, signUpFormInputs, appType]);

  useEffect(() => {
    if (!loginError) return;

    const errorMessage = parseError(loginError);

    if (isAfterSignUp) {
      navigate('/login/mobile-app/MzRlNTgzNGNkMzVka3J2anAwcGpkanAxYmM=');
    } else {
      if (loginError?.message?.includes?.('PasswordResetRequiredException')) {
        message.info('Password reset required for the user', 10);

        navigate('/forgot-password', {
          state: {
            isResetPassword: true,
          },
        });
      } else {
        message.error(errorMessage);
      }
    }
  }, [variable, loginError, isAfterSignUp]);

  useEffect(() => {
    if (!verificationCodeData || !verificationData) return;

    if (verificationCodeData?.verificationCode?.Error) {
      setLoading(false);
      message.error(verificationCodeData.verificationCode.Error.message);
      return;
    }

    const { accessToken, userData } = verificationData;

    navigate('/verification-step-one', {
      state: {
        isVerifyPhoneNumber: true,
        accessToken,
        userData,
      },
    });
  }, [verificationCodeData, verificationData]);

  useEffect(() => {
    if (!verificationCodeError) return;

    const errorMessage = parseError(verificationCodeError);
    message.error(errorMessage);
  }, [verificationCodeError]);

  useEffect(() => {
    if (loginLoading || verificationCodeLoading || hookLoading) {
      setLoading(true);
    }

    if (!loginLoading && !verificationCodeLoading && !hookLoading) {
      setLoading(false);
    }
  }, [loginLoading, verificationCodeLoading, hookLoading]);

  return {
    loading: loading,
    verificationCodeLoading,
    loginData,
    handleLogin,
  };
};
